import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ViewToggle } from '@/components/ui'
import { odooUrl } from '@/lib/utils/odoo-url'
import {
  useCofaAttention,
  useInventoryAttention,
  useOrderAttention,
  usePOAttention,
} from '../api/queries'

type Tab = 'orders' | 'cofa' | 'pos' | 'lots'

export function AttentionRequired() {
  const [tab, setTab] = useState<Tab>('orders')

  return (
    <section className="flex flex-col gap-2">
      <header className="flex items-center justify-between">
        <h2 className="text-role-section-label">
          Attention required
        </h2>
        <ViewToggle
          aria-label="Attention tab"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'orders', label: 'Orders' },
            { value: 'cofa', label: 'CofA' },
            { value: 'pos', label: 'POs' },
            { value: 'lots', label: 'Lots' },
          ]}
        />
      </header>
      {tab === 'orders' ? <OrdersTab /> : null}
      {tab === 'cofa' ? <CofaTab /> : null}
      {tab === 'pos' ? <POsTab /> : null}
      {tab === 'lots' ? <LotsTab /> : null}
    </section>
  )
}

function Shell({
  empty,
  moreHref,
  moreLabel,
  children,
}: {
  empty?: boolean
  moreHref: string
  moreLabel: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-sm border border-border-subtle bg-surface">
      {empty ? (
        <div className="flex min-h-[120px] items-center justify-center p-4 text-center">
          <span className="text-sm text-tertiary">Nothing urgent. Nice.</span>
        </div>
      ) : (
        <div className="overflow-x-auto">{children}</div>
      )}
      <div className="flex justify-end border-t border-border-subtle bg-surface-sunk px-3 py-1.5 text-xs">
        <Link to={moreHref} className="text-link hover:underline">
          {moreLabel} →
        </Link>
      </div>
    </div>
  )
}

function THead({ children }: { children: React.ReactNode }) {
  return (
    <thead className="border-b border-border-subtle bg-surface-sunk">
      <tr className="text-left">{children}</tr>
    </thead>
  )
}

function TH({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-3 py-1.5 text-role-section-label">
      {children}
    </th>
  )
}

function TD({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <td className={`px-3 py-1.5 text-sm text-primary ${className ?? ''}`}>
      {children}
    </td>
  )
}

function OrdersTab() {
  const { data, isLoading } = useOrderAttention()
  const rows = data ?? []
  if (isLoading)
    return (
      <Shell moreHref="/orders" moreLabel="See all overdue orders">
        <SkeletonTable cols={5} />
      </Shell>
    )
  return (
    <Shell empty={rows.length === 0} moreHref="/orders" moreLabel="See all overdue orders">
      <table className="w-full border-collapse text-sm">
        <THead>
          <TH>Customer</TH>
          <TH>SO</TH>
          <TH>Product</TH>
          <TH>Due</TH>
          <TH>Open</TH>
        </THead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.sol_id}
              className="border-b border-border-subtle last:border-b-0 hover:bg-surface-hover"
            >
              <TD>{r.customer}</TD>
              <TD>
                <a
                  href={odooUrl('sale.order', r.so_id)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-link hover:underline"
                >
                  {r.so_number}
                </a>
              </TD>
              <TD className="max-w-[320px] truncate">{r.product_name}</TD>
              <TD>{r.due_on ?? '—'}</TD>
              <TD className="tabular-nums">{r.open.toLocaleString()}</TD>
            </tr>
          ))}
        </tbody>
      </table>
    </Shell>
  )
}

function CofaTab() {
  const { data, isLoading } = useCofaAttention(8)
  const rows = data ?? []
  if (isLoading)
    return (
      <Shell moreHref="/cofa" moreLabel="See CofA Pipeline">
        <SkeletonTable cols={4} />
      </Shell>
    )
  return (
    <Shell empty={rows.length === 0} moreHref="/cofa" moreLabel="See CofA Pipeline">
      <table className="w-full border-collapse text-sm">
        <THead>
          <TH>Lot</TH>
          <TH>Product</TH>
          <TH>Vendor</TH>
          <TH>Age</TH>
        </THead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              className="border-b border-border-subtle last:border-b-0 hover:bg-surface-hover"
            >
              <TD>
                <span >{r.lot_name}</span>
              </TD>
              <TD className="max-w-[280px] truncate">{r.product_name ?? '—'}</TD>
              <TD>{r.vendor_name ?? '—'}</TD>
              <TD className="tabular-nums text-secondary">
                {r.age_seconds !== null ? ageLabel(r.age_seconds) : '—'}
              </TD>
            </tr>
          ))}
        </tbody>
      </table>
    </Shell>
  )
}

function POsTab() {
  const { data, isLoading } = usePOAttention()
  const rows = data ?? []
  if (isLoading)
    return (
      <Shell moreHref="/procurement" moreLabel="See all overdue POs">
        <SkeletonTable cols={5} />
      </Shell>
    )
  return (
    <Shell empty={rows.length === 0} moreHref="/procurement" moreLabel="See all overdue POs">
      <table className="w-full border-collapse text-sm">
        <THead>
          <TH>PO</TH>
          <TH>Vendor</TH>
          <TH>Product</TH>
          <TH>Expected</TH>
          <TH>Open</TH>
        </THead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={`${r.po_id}-${r.po_number}-${r.product_name}`}
              className="border-b border-border-subtle last:border-b-0 hover:bg-surface-hover"
            >
              <TD>
                <a
                  href={odooUrl('purchase.order', r.po_id)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-link hover:underline"
                >
                  {r.po_number}
                </a>
              </TD>
              <TD>{r.vendor}</TD>
              <TD className="max-w-[280px] truncate">{r.product_name}</TD>
              <TD className="text-danger-text">{r.expected_receipt ?? '—'}</TD>
              <TD className="tabular-nums">{r.qty_open.toLocaleString()}</TD>
            </tr>
          ))}
        </tbody>
      </table>
    </Shell>
  )
}

function LotsTab() {
  const { data, isLoading } = useInventoryAttention()
  const oos = data?.oos ?? []
  const expiring = data?.expiring ?? []
  if (isLoading)
    return (
      <Shell moreHref="/inventory" moreLabel="See Inventory">
        <SkeletonTable cols={4} />
      </Shell>
    )
  const empty = oos.length === 0 && expiring.length === 0
  return (
    <Shell empty={empty} moreHref="/inventory" moreLabel="See Inventory">
      <div className="flex flex-col gap-0 divide-y divide-border-subtle">
        {oos.length > 0 ? (
          <div className="p-3">
            <h3 className="mb-1.5 text-2xs font-semibold uppercase tracking-wide text-danger-text">
              Out of stock · {oos.length}
            </h3>
            <table className="w-full border-collapse text-sm">
              <tbody>
                {oos.map((r) => (
                  <tr key={r.product_id} className="border-b border-border-subtle last:border-b-0">
                    <TD className="max-w-[360px] truncate">{r.product_name}</TD>
                    <TD className="text-right tabular-nums">
                      {r.qty_on_hand} {r.uom}
                    </TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {expiring.length > 0 ? (
          <div className="p-3">
            <h3 className="mb-1.5 text-2xs font-semibold uppercase tracking-wide text-warning-text">
              Expiring {'<'} 30d · {expiring.length}
            </h3>
            <table className="w-full border-collapse text-sm">
              <tbody>
                {expiring.map((r) => (
                  <tr
                    key={`${r.lot_name}-${r.product_id}`}
                    className="border-b border-border-subtle last:border-b-0"
                  >
                    <TD>
                      <span >{r.lot_name}</span>
                    </TD>
                    <TD className="max-w-[280px] truncate">{r.product}</TD>
                    <TD className="tabular-nums">{r.expiration_date ?? '—'}</TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </Shell>
  )
}

function SkeletonTable({ cols }: { cols: number }) {
  return (
    <table className="w-full border-collapse text-sm">
      <tbody>
        {Array.from({ length: 5 }).map((_, i) => (
          <tr key={i} className="border-b border-border-subtle last:border-b-0">
            {Array.from({ length: cols }).map((_, j) => (
              <td key={j} className="px-3 py-1.5">
                <span className="inline-block h-3 w-24 animate-pulse rounded-xs bg-muted" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function ageLabel(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`
  if (seconds < 7 * 86400) return `${Math.floor(seconds / 86400)}d`
  return `${Math.floor(seconds / (7 * 86400))}w`
}
